// Écran clé : sur ordinateur, source à gauche, exigence et justification au centre, affectation et traitement à droite.
// Sur mobile : vues successives liste → source → exigence → traitement. Navigation clavier j / k (et flèches dans la liste).
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ClavierReact } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Plus } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, Champ, ChampChoix, ChampTexte, Dialogue, EtatErreur, EtatVide, IndicateurEnregistrement, MarqueIa, Panneau,
  Surligne, useNotifier, type EtatEnregistrement,
} from '../../components/ui.tsx';
import { BadgeStatutExigence, STATUTS_EXIGENCE, BadgeStatutDocument } from '../../components/statuts.tsx';
import { DossierCadre } from './DossierCadre.tsx';
import {
  ACTIONS, BadgeIncertain, DESTINATAIRES, DETECTIONS, Drapeaux, LienSource, NATURES, PARTIES, STATUTS_QUESTION, TYPES_ECART, TYPES_PIECE, repereSource,
  options, type Exigence, type Responsabilite,
} from './commun.tsx';
import { estLocalisationClasseur, extraitAffiche } from '@shared/sources.ts';

type VueMobile = 'liste' | 'source' | 'exigence' | 'traitement';
type Filtres = { statut: string; domaine: string; nature: string; incertaines: string; source: string };

function ChampsFiltres({ filtres, filtrer, domaines, documents }:
  { filtres: Filtres; filtrer: (cle: string, v: string) => void; domaines: [string, string][]; documents: [string, string][] }) {
  return (
    <div className="et-filtres">
      <label className="champ"><span className="champ-libelle">Statut</span>
        <select className="saisie" value={filtres.statut} onChange={(e) => filtrer('statut', e.target.value)}>
          <option value="">Tous</option>{Object.entries(STATUTS_EXIGENCE).map(([k, v]) => <option key={k} value={k}>{v.libelle}</option>)}
        </select></label>
      <label className="champ"><span className="champ-libelle">Domaine</span>
        <select className="saisie" value={filtres.domaine} onChange={(e) => filtrer('domaine', e.target.value)}>
          <option value="">Tous</option>{domaines.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></label>
      <label className="champ"><span className="champ-libelle">Nature</span>
        <select className="saisie" value={filtres.nature} onChange={(e) => filtrer('nature', e.target.value)}>
          <option value="">Toutes</option>{Object.entries(NATURES).map(([k, v]) => <option key={k} value={k}>{v.libelle}</option>)}
        </select></label>
      <label className="champ"><span className="champ-libelle">Pièce source</span>
        <select className="saisie" value={filtres.source} onChange={(e) => filtrer('source', e.target.value)}>
          <option value="">Toutes</option>{documents.map(([k, v]) => <option key={k} value={k}>{v}</option>)}<option value="manuel">Saisies manuelles</option>
        </select></label>
      <label className="case"><input type="checkbox" checked={!!filtres.incertaines} onChange={(e) => filtrer('incertaines', e.target.checked ? '1' : '')} />À vérifier seulement</label>
    </div>
  );
}

export function PageExigences() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { peut } = useSession();
  const [vue, setVue] = useState<VueMobile>(params.get('e') ? 'source' : 'liste');
  const [ajout, setAjout] = useState(false);
  const filtres: Filtres = { statut: params.get('statut') ?? '', domaine: params.get('domaine') ?? '', nature: params.get('nature') ?? '', incertaines: params.get('incertaines') ?? '', source: params.get('source') ?? '' };
  const q = useQuery({ queryKey: ['etudes-exigences', id], queryFn: () => api.get<Exigence[]>(`/api/dossiers/${id}/exigences`) });
  const tout = q.data ?? [];
  const liste = useMemo(() => tout.filter((e) => (!filtres.statut || e.status === filtres.statut) && (!filtres.domaine || e.domain === filtres.domaine)
    && (!filtres.nature || e.nature === filtres.nature) && (!filtres.incertaines || e.uncertain) && (!filtres.source || e.document_id === filtres.source || (filtres.source === 'manuel' && e.detection === 'manuel'))),
  [tout, filtres.statut, filtres.domaine, filtres.nature, filtres.incertaines, filtres.source]);
  const choisieId = params.get('e');
  const choisie = liste.find((e) => e.id === choisieId) ?? tout.find((e) => e.id === choisieId) ?? liste[0] ?? null;
  const index = choisie ? liste.findIndex((e) => e.id === choisie.id) : -1;
  const listeRef = useRef<HTMLDivElement>(null);
  const nbFiltres = Object.values(filtres).filter(Boolean).length;

  const choisir = (e: Exigence | undefined, versVue?: VueMobile) => {
    if (!e) return;
    const p = new URLSearchParams(params); p.set('e', e.id); setParams(p, { replace: true });
    if (versVue) changerVue(versVue);
    requestAnimationFrame(() => listeRef.current?.querySelector<HTMLElement>(`[data-id="${e.id}"]`)?.scrollIntoView({ block: 'nearest' }));
  };
  // Sur téléphone, changer de vue ramène en haut de la vue (sinon on resterait au milieu de la liste).
  function changerVue(v: VueMobile) {
    setVue(v);
    requestAnimationFrame(() => { const el = document.querySelector('.et-exigences'); if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start' }); });
  }
  const filtrer = (cle: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(cle, v); else p.delete(cle); setParams(p, { replace: true }); };

  // j / k : exigence suivante / précédente (hors saisie)
  useEffect(() => {
    const touche = (ev: KeyboardEvent) => {
      const cible = ev.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(cible?.tagName) || cible?.isContentEditable || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (ev.key === 'j') { ev.preventDefault(); choisir(liste[Math.min(liste.length - 1, index + 1)]); }
      if (ev.key === 'k') { ev.preventDefault(); choisir(liste[Math.max(0, index - 1)]); }
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  });
  const focaliser = (eid?: string) => requestAnimationFrame(() => listeRef.current?.querySelector<HTMLElement>(`[data-id="${eid}"]`)?.focus());
  const flechesListe = (ev: ClavierReact) => {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); const s = liste[Math.min(liste.length - 1, index + 1)]; choisir(s); focaliser(s?.id); }
    if (ev.key === 'ArrowUp') { ev.preventDefault(); const s = liste[Math.max(0, index - 1)]; choisir(s); focaliser(s?.id); }
  };

  const domaines = useMemo(() => [...new Map(tout.filter((e) => e.domain).map((e) => [e.domain!, e.domaine_libelle ?? e.domain!])).entries()], [tout]);
  const documents = useMemo(() => [...new Map(tout.filter((e) => e.document_id).map((e) => [e.document_id!, `${TYPES_PIECE[e.doc_type ?? ''] ?? ''} — ${e.document_title}`])).entries()], [tout]);
  const libelleFiltres = `Filtres${nbFiltres ? ` (${nbFiltres} actif${nbFiltres > 1 ? 's' : ''})` : ''}`;
  const champsFiltres = <ChampsFiltres filtres={filtres} filtrer={filtrer} domaines={domaines} documents={documents} />;

  return (
    <DossierCadre dossierId={id} actif="exigences" large
      actions={peut('dossier.ecrire') ? <Bouton icone={<Plus aria-hidden />} onClick={() => setAjout(true)}>Ajouter une exigence</Bouton> : undefined}>
      {q.isLoading && <Chargement lignes={6} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && tout.length === 0 && (
        <Panneau><EtatVide icone={<ListChecks />} titre="Aucune exigence" texte="Les exigences apparaissent après l’analyse des pièces (onglet Synthèse), ou par saisie manuelle." /></Panneau>
      )}
      {q.data && tout.length > 0 && (
        <div className="et-exigences" data-vue={vue}>
          {/* Ordinateur : liste filtrable AU-DESSUS des trois colonnes (sélecteur, précédente / suivante, filtres). */}
          <div className="et-barre-exigence">
            <label className="champ et-choix-exigence">
              <span className="champ-libelle">Exigence {index + 1} sur {liste.length}{nbFiltres ? ' (filtrées)' : ''} — touches j et k pour naviguer</span>
              <select className="saisie" value={choisie?.id ?? ''} onChange={(ev) => choisir(liste.find((x) => x.id === ev.target.value) ?? tout.find((x) => x.id === ev.target.value))}>
                {liste.map((e) => <option key={e.id} value={e.id}>{e.label} — {STATUTS_EXIGENCE[e.status]?.libelle}{e.in_quote === false ? ' — non chiffré' : ''}{e.uncertain ? ' — à vérifier' : ''}{repereSource(e.source_page, e.source_locator) ? ` — ${repereSource(e.source_page, e.source_locator)}` : ''}</option>)}
                {choisie && !liste.some((e) => e.id === choisie.id) && <option value={choisie.id}>{choisie.label}</option>}
              </select>
            </label>
            <div className="groupe-btn">
              <Bouton disabled={index <= 0} onClick={() => choisir(liste[index - 1])}>Précédente</Bouton>
              <Bouton disabled={index < 0 || index >= liste.length - 1} onClick={() => choisir(liste[index + 1])}>Suivante</Bouton>
            </div>
            <details className="et-filtres-bureau">
              <summary className="btn">{libelleFiltres}</summary>
              {champsFiltres}
            </details>
          </div>

          <nav className="et-vues-mobile" aria-label="Vues de l’exigence">
            <div className="segment et-segment" role="group">
              {(['liste', 'source', 'exigence', 'traitement'] as VueMobile[]).map((v) => (
                <button key={v} type="button" aria-pressed={vue === v} onClick={() => changerVue(v)} disabled={v !== 'liste' && !choisie}>
                  {{ liste: `Liste (${liste.length})`, source: 'Source', exigence: 'Exigence', traitement: 'Traitement' }[v]}
                </button>
              ))}
            </div>
          </nav>

          <section className="et-col et-col-liste" aria-label="Liste des exigences">
            <details className="et-filtres-details">
              <summary>{libelleFiltres}</summary>
              {champsFiltres}
            </details>
            <div className="et-liste-exigences" ref={listeRef} role="listbox" aria-label="Exigences" onKeyDown={flechesListe}>
              {liste.map((e) => (
                <button key={e.id} type="button" role="option" data-id={e.id} aria-selected={choisie?.id === e.id} tabIndex={choisie?.id === e.id ? 0 : -1}
                  className="et-item" onClick={() => choisir(e, 'source')}>
                  <span className="et-item-titre">{e.label}</span>
                  <span className="et-item-meta">
                    <BadgeStatutExigence statut={e.status} />
                    {e.in_quote === false && <span className="tres-petit et-texte-ambre">Non chiffré</span>}
                    {e.uncertain && <span className="tres-petit et-texte-ambre">À vérifier</span>}
                    <span className="tres-petit discret">{(estLocalisationClasseur(e.source_locator) ? e.source_locator!.replace(/^feuille « .* », /, '') : e.source_page ? `p. ${e.source_page}` : null) ?? DETECTIONS[e.detection]}</span>
                  </span>
                </button>
              ))}
              {liste.length === 0 && <EtatVide titre="Aucune exigence ne correspond" texte="Modifiez les filtres." />}
            </div>
          </section>

          {choisie ? <>
            <ColonneSource e={choisie} suivante={() => changerVue('exigence')} />
            <ColonneExigence e={choisie} suivante={() => changerVue('traitement')} />
            <ColonneTraitement key={choisie.id} e={choisie} dossierId={id} />
            <nav className="et-precsuiv" aria-label="Exigence précédente ou suivante">
              <Bouton disabled={index <= 0} onClick={() => choisir(liste[index - 1])}>Précédente</Bouton>
              <span className="petit discret">{index + 1} sur {liste.length}</span>
              <Bouton disabled={index < 0 || index >= liste.length - 1} onClick={() => choisir(liste[index + 1])}>Suivante</Bouton>
            </nav>
          </> : <section className="et-col et-col-vide"><EtatVide titre="Aucune exigence ne correspond aux filtres" /></section>}
        </div>
      )}
      <DialogueAjout ouvert={ajout} fermer={() => setAjout(false)} dossierId={id} creee={(eid) => { setAjout(false); q.refetch().then(() => { const p = new URLSearchParams(params); p.set('e', eid); setParams(p); }); }} />
    </DossierCadre>
  );
}

function ColonneSource({ e, suivante }: { e: Exigence; suivante: () => void }) {
  const saisie = !e.source_version_id && e.source_locator === 'demande saisie';
  return (
    <section className="et-col et-col-source" aria-labelledby="et-titre-source">
      <h2 id="et-titre-source" className="et-col-titre">Source</h2>
      {e.source_version_id || saisie ? (
        <div className="pile">
          <dl className="et-fiche">
            <div><dt>Pièce</dt><dd>{saisie ? 'Demande du client (saisie du dossier)' : <>{e.document_title} <span className="discret">— {TYPES_PIECE[e.doc_type ?? ''] ?? e.doc_type}</span></>}</dd></div>
            {!saisie && <div><dt>Version</dt><dd className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}><span className="mono">{e.version_label}</span>{e.version_status && e.version_status !== 'valide' && <BadgeStatutDocument statut={e.version_status} />}</dd></div>}
            {e.source_page && !estLocalisationClasseur(e.source_locator) && <div><dt>Page</dt><dd>{e.source_page}</dd></div>}
            {e.source_locator && !saisie && <div><dt>Localisation</dt><dd>{e.source_locator}</dd></div>}
          </dl>
          {e.version_status === 'obsolete' && <Alerte ton="ambre">Cette version de la pièce a été remplacée : vérifiez l’extrait dans la version actuelle.</Alerte>}
          <div className="extrait et-extrait" aria-label="Extrait de la pièce, phrase concernée surlignée">
            {e.contexte ? <Surligne texte={e.contexte} /> : <mark>{extraitAffiche(e.source_excerpt)}</mark>}
          </div>
          {e.source_ocr && <MarqueIa />}
          {!saisie && <LienSource versionId={e.source_version_id} page={e.source_page} chunkId={e.chunk_id} titre={e.document_title ?? 'Pièce'} localisation={e.source_locator} />}
        </div>
      ) : (
        <EtatVide titre="Source manquante" texte={e.detection === 'manuel' ? 'Exigence saisie sans pièce source.' : 'La pièce source n’est plus disponible (supprimée ou remplacée).'} />
      )}
      <div className="et-mobile-seulement"><Bouton onClick={suivante}>Voir l’exigence</Bouton></div>
    </section>
  );
}

function ColonneExigence({ e, suivante }: { e: Exigence; suivante: () => void }) {
  const detail = useQuery({ queryKey: ['etudes-exigence', e.id], queryFn: () => api.get<Exigence & { ecarts: { id: string; kind: string; title: string; status: string }[] }>(`/api/exigences/${e.id}`) });
  return (
    <section className="et-col et-col-exigence" aria-labelledby="et-titre-exigence">
      <h2 id="et-titre-exigence" className="et-col-titre">Exigence</h2>
      <div className="pile">
        <h3 className="et-exigence-titre">{e.label}</h3>
        <div className="rangee et-badges">
          <BadgeStatutExigence statut={e.status} />
          <Badge ton="info" icone={false}>{NATURES[e.nature]?.libelle ?? e.nature}</Badge>
          {e.domaine_libelle && <Badge icone={false}>{e.domaine_libelle}</Badge>}
        </div>
        {e.uncertain && <BadgeIncertain />}
        <Drapeaux e={e} />
        <p className="tres-petit discret">{NATURES[e.nature]?.aide}.</p>
        <div className="et-regle">
          <span className="tres-petit discret">{DETECTIONS[e.detection]}</span>
          <strong className="petit">{e.regle.libelle}</strong>
          {e.regle.explication && <p className="petit secondaire">{e.regle.explication}</p>}
        </div>
        {e.exclusion && (
          <div className="et-exclusion"><span className="tres-petit discret">Exclusion proposée (à confirmer, jamais appliquée d’office)</span><p className="petit">{e.exclusion}</p></div>
        )}
        {e.detail && <div><span className="tres-petit discret">Détail</span><p className="petit">{e.detail}</p></div>}
        {detail.data?.ecarts.length ? (
          <div className="pile" style={{ gap: 6 }}>
            <span className="tres-petit discret">Écarts liés</span>
            <ul className="et-liste-simple">
              {detail.data.ecarts.map((g) => <li key={g.id} className="petit"><Badge ton={TYPES_ECART[g.kind]?.ton ?? 'neutre'}>{TYPES_ECART[g.kind]?.libelle ?? g.kind}</Badge> {g.title}</li>)}
            </ul>
          </div>
        ) : null}
      </div>
      <div className="et-mobile-seulement"><Bouton onClick={suivante}>Voir le traitement</Bouton></div>
    </section>
  );
}

function ColonneTraitement({ e, dossierId }: { e: Exigence; dossierId: string }) {
  const { peut } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const qualifier = peut('exigence.valider');
  const ecrire = peut('dossier.ecrire');
  const [statut, setStatut] = useState(e.status);
  const [lot, setLot] = useState(e.other_lot ?? '');
  const [confirme, setConfirme] = useState(e.other_lot_confirmed);
  const [detail, setDetail] = useState(e.detail ?? '');
  const [resp, setResp] = useState<Record<string, Responsabilite>>(() => Object.fromEntries(e.responsabilites.map((r) => [r.action, r])));
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [aide, setAide] = useState<string | null>(null);
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined);
  const questions = useQuery({ queryKey: ['etudes-exigence', e.id], queryFn: () => api.get<any>(`/api/exigences/${e.id}`) });
  const [question, setQuestion] = useState({ text: '', addressee: 'maitre_oeuvre' });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['etudes-exigences', dossierId] });
    qc.invalidateQueries({ queryKey: ['etudes-exigence', e.id] });
    qc.invalidateQueries({ queryKey: ['etudes-synthese', dossierId] });
  };
  const envoyer = async (corps: Record<string, unknown>) => {
    setEtat('en_cours'); setAide(null);
    try { await api.patch(`/api/exigences/${e.id}`, corps); setEtat('enregistre'); rafraichir(); }
    catch (err) {
      if (err instanceof ErreurApi && err.status === 400) { setEtat('modifie'); setAide(err.message); } else { setEtat('erreur'); notifier(messageErreur(err), true); }
    }
  };
  const changerStatut = (s: string) => {
    setStatut(s);
    if (s === 'autre_lot' && (!lot.trim() || !confirme)) { setEtat('modifie'); setAide('Indiquez le lot et cochez la confirmation pour enregistrer l’affectation.'); return; }
    envoyer(s === 'autre_lot' ? { status: s, otherLot: lot.trim(), otherLotConfirmed: confirme } : { status: s });
  };
  const changerLot = (l: string, c: boolean) => {
    setLot(l); setConfirme(c);
    if (statut !== 'autre_lot') return;
    if (!l.trim() || !c) { setEtat('modifie'); setAide('Indiquez le lot et cochez la confirmation pour enregistrer l’affectation.'); return; }
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => envoyer({ status: 'autre_lot', otherLot: l.trim(), otherLotConfirmed: c }), 600);
  };
  const changerDetail = (v: string) => {
    setDetail(v); setEtat('modifie');
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => envoyer({ detail: v || null }), 800);
  };
  const changerResp = async (action: string, party: string, nom?: string) => {
    const suivant = { ...resp, [action]: { action, party, party_name: nom ?? resp[action]?.party_name ?? null } };
    setResp(suivant); setEtat('en_cours');
    try {
      await api.put(`/api/exigences/${e.id}/responsabilites`, { responsabilites: Object.values(suivant).map((r) => ({ action: r.action, party: r.party, partyName: r.party_name || null })) });
      setEtat('enregistre'); rafraichir();
    } catch (err) { setEtat('erreur'); notifier(messageErreur(err), true); }
  };
  const ajouterQuestion = async () => {
    if (question.text.trim().length < 3) return;
    try {
      await api.post(`/api/dossiers/${dossierId}/questions`, { text: question.text.trim(), addressee: question.addressee, requirementId: e.id });
      setQuestion({ ...question, text: '' }); notifier('Question ajoutée.');
      qc.invalidateQueries({ queryKey: ['etudes-exigence', e.id] }); qc.invalidateQueries({ queryKey: ['etudes-questions', dossierId] });
    } catch (err) { notifier(messageErreur(err), true); }
  };

  return (
    <section className="et-col et-col-traitement" aria-labelledby="et-titre-traitement">
      <div className="entre" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h2 id="et-titre-traitement" className="et-col-titre">Affectation et traitement</h2>
        {ecrire && <IndicateurEnregistrement etat={etat} />}
      </div>
      {!qualifier && <Alerte ton="info">Lecture seule : la qualification des exigences demande le droit de validation (chargé d’affaires ou référent).</Alerte>}
      <fieldset className="et-statuts" disabled={!qualifier}>
        <legend className="champ-libelle">Statut</legend>
        {Object.entries(STATUTS_EXIGENCE).map(([k, v]) => (
          <label key={k} className={`et-statut ${statut === k ? 'et-statut-actif' : ''}`}>
            <input type="radio" name={`statut-${e.id}`} value={k} checked={statut === k} onChange={() => changerStatut(k)} />
            <span>{v.libelle}</span>
          </label>
        ))}
      </fieldset>
      {statut === 'autre_lot' && (
        <div className="pile et-autre-lot">
          <Champ libelle="Lot concerné" value={lot} disabled={!qualifier} onChange={(ev) => changerLot(ev.target.value, confirme)} placeholder="ex. Plomberie / gaz, CVC" />
          <label className="case"><input type="checkbox" checked={confirme} disabled={!qualifier} onChange={(ev) => changerLot(lot, ev.target.checked)} />
            Affectation confirmée par écrit (maître d’œuvre ou lot concerné)</label>
        </div>
      )}
      {aide && <Alerte ton="ambre">{aide}</Alerte>}
      {e.validated_at && <p className="tres-petit discret">Qualifiée par {e.validated_by_name ?? 'un utilisateur'} le {dateHeure(e.validated_at)}.</p>}
      <ChampTexte libelle="Détail, hypothèse ou réserve" value={detail} readOnly={!ecrire} rows={3} onChange={(ev) => changerDetail(ev.target.value)} />

      <div className="pile" style={{ gap: 6 }}>
        <span className="champ-libelle">Qui fait quoi ?</span>
        <div className="et-resp">
          {ACTIONS.map((a) => {
            const r = resp[a.code];
            return (
              <div key={a.code} className="et-resp-ligne">
                <label className="et-resp-action" htmlFor={`resp-${e.id}-${a.code}`}>{a.libelle}</label>
                <select id={`resp-${e.id}-${a.code}`} className="saisie" value={r?.party ?? 'inconnu'} disabled={!ecrire} onChange={(ev) => changerResp(a.code, ev.target.value)}>
                  {options(PARTIES).map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
                </select>
                {(r?.party === 'autre_lot' || r?.party === 'client') && (
                  <input className="saisie et-resp-nom" aria-label={`${a.libelle} : nom`} placeholder={r.party === 'client' ? 'Client' : 'Lot'} defaultValue={r.party_name ?? ''} disabled={!ecrire}
                    onBlur={(ev) => { if (ev.target.value !== (r.party_name ?? '')) changerResp(a.code, r.party, ev.target.value); }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="pile" style={{ gap: 6 }}>
        <span className="champ-libelle">Questions liées</span>
        {questions.data?.questions?.length ? (
          <ul className="et-liste-simple">
            {questions.data.questions.map((x: any) => <li key={x.id} className="petit"><Badge icone={false}>{STATUTS_QUESTION[x.status] ?? x.status}</Badge> {x.text}{x.answer && <span className="discret"> — Réponse : {x.answer}</span>}</li>)}
          </ul>
        ) : <p className="petit discret">Aucune question liée.</p>}
        {ecrire && (
          <div className="pile et-question-ajout">
            <ChampTexte libelle="Nouvelle question" rows={2} value={question.text} onChange={(ev) => setQuestion({ ...question, text: ev.target.value })} />
            <div className="rangee" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <ChampChoix libelle="Destinataire" value={question.addressee} onChange={(ev) => setQuestion({ ...question, addressee: ev.target.value })} options={options(DESTINATAIRES)} />
              <Bouton onClick={ajouterQuestion} disabled={question.text.trim().length < 3}>Ajouter la question</Bouton>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function DialogueAjout({ ouvert, fermer, dossierId, creee }: { ouvert: boolean; fermer: () => void; dossierId: string; creee: (id: string) => void }) {
  const notifier = useNotifier();
  const pieces = useQuery({ queryKey: ['etudes-pieces', dossierId], enabled: ouvert, queryFn: () => api.get<{ pieces: any[] }>(`/api/dossiers/${dossierId}/pieces`) });
  const [f, setF] = useState({ label: '', nature: 'fait', version: '', page: '', locator: '', excerpt: '', detail: '' });
  const [envoi, setEnvoi] = useState(false);
  const versions = (pieces.data?.pieces ?? []).flatMap((p) => (p.versions as any[]).filter((v) => v.status === 'valide').map((v) => ({ valeur: v.id, libelle: `${p.title} — ${v.version_label}` })));
  const enregistrer = async () => {
    if (!f.label.trim()) return;
    setEnvoi(true);
    try {
      const r = await api.post<{ id: string }>(`/api/dossiers/${dossierId}/exigences`, {
        label: f.label, nature: f.nature, detail: f.detail || null, sourceVersionId: f.version || null,
        sourcePage: f.version && f.page ? Number(f.page) : null, sourceLocator: f.locator || null, sourceExcerpt: f.excerpt || null,
      });
      setF({ label: '', nature: 'fait', version: '', page: '', locator: '', excerpt: '', detail: '' });
      notifier('Exigence ajoutée.');
      creee(r.id);
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Ajouter une exigence" ouvert={ouvert} fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} disabled={!f.label.trim()} onClick={enregistrer}>Ajouter l’exigence</Bouton></>}>
      <div className="pile">
        <Champ libelle="Exigence" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="ex. Intégration des compteurs à la supervision" />
        <ChampChoix libelle="Nature" value={f.nature} onChange={(e) => setF({ ...f, nature: e.target.value })} options={Object.entries(NATURES).map(([valeur, v]) => ({ valeur, libelle: `${v.libelle} — ${v.aide}` }))} />
        <ChampChoix libelle="Pièce source" value={f.version} vide="Aucune (saisie sans source)" onChange={(e) => setF({ ...f, version: e.target.value })} options={versions} />
        {f.version && (
          <div className="grille-form">
            <Champ libelle="Page" inputMode="numeric" value={f.page} onChange={(e) => setF({ ...f, page: e.target.value.replace(/\D/g, '') })} />
            <Champ libelle="Localisation" value={f.locator} placeholder="§ 4.2, ligne 12…" onChange={(e) => setF({ ...f, locator: e.target.value })} />
          </div>
        )}
        {f.version && <ChampTexte libelle="Extrait recopié" rows={3} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} />}
        <ChampTexte libelle="Détail" rows={2} value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} />
      </div>
    </Dialogue>
  );
}
